import { setAccountToHeader } from 'api/fetcher';
import { AxiosError } from 'axios';
import { replace } from 'connected-react-router';
import crypto from 'crypto';
import numeral from 'numeral';
import { UrlObject } from 'url';
import { FROM_HH, HH_LOGIN_LINK } from 'utils/constants/app';
import {
    BANNER_IMAGE_CROP_DEFAULT,
    BANNER_IMAGE_CROP_TYPES,
    BANNER_IMAGE_CROP_TYPES_CONFIG,
    BannerImageCropType,
    ImageComponent,
    UpscaleStatus,
} from 'utils/constants/banner';
import {
    CAMPAIGN_SOURCES_WITH_UNKNOWN_AGE,
    CampaignDeviceType,
    CampaignGender,
    CampaignLanguage,
    CampaignSource,
    CampaignSubSource,
    FIRST_PLACE_REGIONS, // exp_PORTFOLIO-53635_next_line
    HH_SUB_SOURCES,
    VK_ADS_SUB_SOURCES,
    YANDEX_SUB_SOURCES,
} from 'utils/constants/campaign';
import { NON_BREAKING_SPACE, SortType } from 'utils/constants/common';
import { Settings } from 'utils/constants/dynamicSettings';
import { READ_ONLY_ROUTES } from 'utils/constants/methods';
import { Permissions } from 'utils/constants/permissions';
import { ADMINS, CURRENT_ACCOUNT_ID_STORAGE } from 'utils/constants/user';
import { getDashboardRoute } from 'utils/dashboardRoute';
import { getSettingsValue } from 'utils/settings';

import { localStorageWrapper, sessionStorageWrapper } from '@hh.ru/browser-api-utils';

import NumberFormatter from 'bloko/common/numberFormatter';

import type { ProfRoles, Region } from 'store/autoReducers';
import type { User as UserFromAccounts } from 'store/models/accounts';
import { Act, ActType } from 'store/models/acts';
import type { ClickmeConfig } from 'store/models/clickme';
import type { Images } from 'store/models/createBanner';
import type { CreateCampaignState, SalaryRange } from 'store/models/createCampaign';
import type { Account, User as UserFromUser } from 'store/models/user';
import { AccountStatus, SessionType } from 'store/models/user';
import { AppThunkAction } from 'store/utils';

interface GetIndexByEntityId {
    <T, F extends keyof T>(entityId: T[F], entities: T[], field: F): number;
}

export const getIndexByEntityId: GetIndexByEntityId = (entityId, entities = [], field) => {
    let index = -1;
    for (let key = 0; key < entities.length; key++) {
        if (entities[key][field] === entityId) {
            index = key;
            break;
        }
    }
    return index;
};

interface GetChunks {
    <T>(source: T[], size: number): T[][];
}

export const getChunks: GetChunks = (source = [], size = 0) => {
    const chunks = [];
    for (let i = 0, j = source.length; i < j; i += size) {
        chunks.push(source.slice(i, i + size));
    }
    return chunks;
};

interface AggregateData {
    all: number;
    clickme: number;
    hh: number;
    counts: {
        clickme: number;
        hh: number;
    };
}

type AggregateActs = (acts: Act[]) => AggregateData;

export const aggregateActs: AggregateActs = (acts) => {
    const data = { all: 0, clickme: 0, hh: 0, counts: { clickme: 0, hh: 0 } };
    acts.forEach(({ amount, correctedAmount, type }) => {
        const rightAmount = correctedAmount !== null ? correctedAmount : amount;
        data.all += rightAmount;
        if (type === ActType.Clickme) {
            data.clickme += rightAmount;
            data.counts.clickme += 1;
        }
        if (type === ActType.HH) {
            data.hh += rightAmount;
            data.counts.hh += 1;
        }
    });
    return data;
};

type TDownloadCSV = (fileName: string, csv: string) => void;

export const downloadCSV: TDownloadCSV = (fileName, csv) => {
    const BOM = '\uFEFF';
    const code = 'data:text/csv;charset=utf-8,';
    const encodedCsv = encodeURI(code + BOM + csv);
    const link = document.createElement('a');
    link.setAttribute('href', encodedCsv);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

interface ToCSV {
    <T>(filename: string, items: T[], headers: readonly (keyof T)[], total: (string | number)[]): void;
}

export const toCSV: ToCSV = (filename, items, headers, total) => {
    const rows = [];
    const headersCVS: string[] = [];
    const decode = (val: unknown) => (typeof val === 'number' || typeof val === 'string' ? `"${val}"` : '""');
    headers.map((header) => {
        return headersCVS.push(decode(header));
    });
    rows.push(headersCVS);
    items.map((item) => {
        const contents: string[] = [];
        headers.map((key) => {
            return contents.push(decode(item[key]));
        });
        if (contents.length > 0) {
            rows.push(contents);
        }
        return null;
    });
    if (total.length > 0) {
        rows.push(total);
    }
    const csv = rows.map((e) => e.join(';')).join('\n');
    downloadCSV(filename, csv);
};

type UserBasicGuard = (err: AxiosError) => Permissions[];

export const userBasicGuard: UserBasicGuard = (err) => {
    const permissions = [];
    if (err?.response?.status === 403) {
        permissions.push(Permissions.PermissionDenied);
    }
    if (err?.response?.status === 401) {
        permissions.push(Permissions.UserNotAuthorized);
    }
    return permissions;
};

type GetSessionType = (user: UserFromUser) => SessionType;

export const getSessionType: GetSessionType = (user) => {
    return user && ADMINS.includes(user.userType) ? SessionType.Sudo : SessionType.Typical;
};

type GetLowerCaseSelector = (str: string) => string;

export const getLowerCaseSelector: GetLowerCaseSelector = (str) => {
    return str.toLowerCase().replace('_', '');
};

interface Mapping<T> {
    from: keyof T;
    to: string;
}

interface ConvertToTree {
    <T>(source: T[], mapping: readonly Mapping<T>[], maxDepth?: number): Record<string, unknown | unknown[]>[];
}

/**
 * Возвращает структуру для создания дерева.
 */
export const convertToTree: ConvertToTree = (source, mapping, maxDepth = 1) => {
    const result: Record<string, unknown | unknown[]>[] = [];

    source.forEach((item) => {
        const newItem: Record<string, unknown | unknown[]> = {};
        mapping.forEach((fieldMap) => {
            const itemFrom = item[fieldMap.from];
            if (typeof itemFrom === 'number' || typeof itemFrom === 'string') {
                newItem[fieldMap.to] = itemFrom.toString();
            }

            if (maxDepth > 1 && itemFrom && Array.isArray(itemFrom) && itemFrom.length) {
                newItem[fieldMap.to] = convertToTree(itemFrom, mapping, maxDepth - 1);
            }
        });
        result.push(newItem);
    });
    return result;
};

interface Sort<T> {
    key: keyof T;
    sort: SortType;
}

interface SortEntitiesData<T> {
    newItems: T[];
    newSorts: Sort<T>[];
}

interface Compare {
    <T>(actA: T, actB: T, sortBy: keyof T): -1 | 0 | 1;
}

interface SortEntities {
    <T>(items: T[], sorts: Sort<T>[], sortBy: keyof T): SortEntitiesData<T>;
}

/**
 * Возвращает отсортированный массив и новое состояние сортировки (один элемент).
 */
export const sortEntities: SortEntities = (items, sorts, sortBy) => {
    const newItems = items.slice();
    let newSort = SortType.ASC;
    if (sorts.length > 0) {
        sorts.forEach(({ key, sort }) => {
            if (key === sortBy) {
                newSort = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
            }
        });
    }
    const compare: Compare = (itemA, itemB, sortBy) => {
        const a = itemA[sortBy];
        const b = itemB[sortBy];
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;
        if (a === b) return 0;
        const isAGreater = newSort === SortType.ASC ? a > b : a < b;
        return isAGreater ? 1 : -1;
    };
    newItems.sort((actA, actB) => compare(actA, actB, sortBy));
    return {
        newItems,
        newSorts: [{ key: sortBy, sort: newSort }],
    };
};

type AddOrDelete = (source: string[], value: string, add: boolean) => string[];

export const addOrDelete: AddOrDelete = (source, value, add) => {
    const newSource = source.slice();
    const isSourceIncludesValue = source.includes(value);
    if (add && !isSourceIncludesValue) {
        newSource.push(value);
    } else if (isSourceIncludesValue) {
        newSource.splice(newSource.indexOf(value), 1);
    }
    return newSource;
};

type GetSourcesByUnknownAge = (sources: CampaignSubSource[], unknownAge: boolean) => CampaignSubSource[];

/**
 * Возвращает массив площадок на основе значения флага "Возраст скрыт или неизвествен"
 */
export const getSourcesByUnknownAge: GetSourcesByUnknownAge = (sources, unknownAge) => {
    if (unknownAge) {
        return sources;
    }
    return sources.filter((source) => !CAMPAIGN_SOURCES_WITH_UNKNOWN_AGE.includes(source));
};

export const sortProfRoles = (profRoles: ProfRoles[]): ProfRoles[] => {
    profRoles.forEach(({ professionalRoles }) => professionalRoles && sortProfRoles(professionalRoles));

    return profRoles.sort((pA, pB) => (pA.name > pB.name ? 1 : -1));
};

type SortRegions = (sources: Region[]) => Region[];

const sortRegions: SortRegions = (regions) =>
    regions.sort((rA, rB) => {
        const aIndex = FIRST_PLACE_REGIONS.indexOf(rA.id);
        const bIndex = FIRST_PLACE_REGIONS.indexOf(rB.id);
        const wasAFound = aIndex !== -1;
        const wasBFound = bIndex !== -1;

        if (!wasAFound && !wasBFound) return rA.name > rB.name ? 1 : -1;

        if (wasAFound && !wasBFound) return -1;
        else if (wasBFound && !wasAFound) return 1;

        return aIndex > bIndex ? 1 : -1;
    });

/**
 * Сортирует regions по алфавиту и ставит Москву на первое место
 */
export const sortAreas: SortRegions = (regions) => {
    regions.forEach((region) => {
        if (region.areas.length) region.areas = sortAreas(region.areas);
    });

    return sortRegions(regions);
};

type GetClicksByBudget = (budget: number | string, pricePerClick: number) => number;

export const getClicksByBudget: GetClicksByBudget = (budget, pricePerClick) => {
    return pricePerClick > 0 ? Math.floor(Number(budget) / pricePerClick) : 0;
};

type GetRandomInt = (min: number, max: number) => number;

export const getRandomInt: GetRandomInt = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

type NumberRange = (start: number, end: number) => number[];

/**
 * Возвращает массив от start до end
 */
export const numberRange: NumberRange = (start, end) => {
    return new Array(end - start + 1).fill(0).map((_, i) => i + start);
};

type VacancyStatisticsHHLink = (vacancyId: number) => string;

export const vacancyStatisticsHHLink: VacancyStatisticsHHLink = (vacancyId) =>
    `https://hh.ru/employer/statistics/vacancy_conversion?vacancyId=${vacancyId}`;

type ConvertFieldsToString = (obj: Record<string, number | string>) => Record<string, string>;

export const convertFieldsToString: ConvertFieldsToString = (obj) => {
    const newObj: Record<string, string> = {};
    Object.keys(obj).forEach((key) => (newObj[key] = `${obj[key]}`));
    return newObj;
};

interface HashByCampaign {
    regions: number[];
    sex: CampaignGender;
    apiSources: CampaignSubSource[];
    ageMin: number;
    ageMax: number;
    budget: CreateCampaignState['adTotalBudget'];
    budgetDaily: CreateCampaignState['adDailyBudget'];
    title: string;
    salaryRange: SalaryRange | null;
    language: CampaignLanguage[];
    deviceType: CampaignDeviceType;
    includeAnonymousAge: boolean;
    professionalRoles?: string[];
}

type GetHashByCampaign = (obj: Partial<HashByCampaign>) => string;

export const getHashByCampaign: GetHashByCampaign = ({
    regions,
    sex,
    apiSources,
    ageMin,
    ageMax,
    budget,
    budgetDaily,
    title,
    salaryRange,
    language,
    deviceType,
    includeAnonymousAge,
    professionalRoles,
}) => {
    const params = [
        regions?.join(','),
        (professionalRoles as string[]).join(','),
        sex,
        apiSources?.join(','),
        ageMin,
        ageMax,
        budget,
        budgetDaily,
        title,
        salaryRange?.min ?? '',
        salaryRange?.max ?? '',
        language?.join(','),
        deviceType,
        includeAnonymousAge?.toString(),
    ];
    return crypto.createHash('md5').update(params.join(';')).digest('hex');
};

numeral.localeData().delimiters.thousands = NON_BREAKING_SPACE;

type GetFormattedNumber = (amount: number) => string;

export const getFormattedIntNumber: GetFormattedNumber = (amount) => numeral(amount).format('0,0');
export const getFormattedNumber: GetFormattedNumber = (amount) => numeral(amount).format('0,0.00');
export const getFormattedNumberWithPercent: GetFormattedNumber = (amount) => `${numeral(amount).format('0')}%`;

/**
 * Округляет переданное число до двух знаков после запятой.
 * Оставляет десятичные только если они присутствуют после округления.
 * Если передан параметр forceShowCents, то два знака после запятой будут показаны в любом случае.
 * @example
 * 2 -> '2'
 * 2.00 -> '2'
 * 2.9033 -> '2,90'
 * 2.1488 -> '2,15'
 * 2340.543 -> '2 340,54'
 */
export const formatCost = (cost: number, groupSeparator?: string, forceShowCents?: boolean): string => {
    const roundedCost = cost % 1 || forceShowCents ? Number(cost).toFixed(2) : `${cost}`;
    return NumberFormatter.format(roundedCost, groupSeparator ? { groupSeparator } : {});
};
/**
 * Форматирует целочисленную цену в копейках (1 рубль == 100).
 * Используется в биллинге для исключения ошибок округления.
 * Если передан параметр forceShowCents, то два знака после запятой будут показаны в любом случае.
 * @example
 * 200 -> '2'
 * 200.00 -> '2'
 * 290.33 -> '2,90'
 * 214.88 -> '2,15'
 * 234054.3 -> '2 340,54'
 */
export const formatCostInCents = (cost: number, groupSeparator?: string, forceShowCents?: boolean): string => {
    return formatCost(cost / 100, groupSeparator, forceShowCents);
};

type GetCTR = (ctr: number) => string;

export const getCTR: GetCTR = (ctr) => formatCost(ctr * 100);

export const CENTS_IN_RUB = 100;

export const convertCentsToRubles = (value: string, decimalLength: number): string => {
    if (!value) {
        return '';
    }

    const parsedValue = parseFloat(value);

    if (isNaN(parsedValue)) {
        return '';
    }

    return (parsedValue / CENTS_IN_RUB).toFixed(decimalLength);
};

export const convertRublesToCents = (value: string): string => {
    const parsedValue = parseFloat(value);

    if (isNaN(parsedValue)) {
        return '';
    }

    return (parsedValue * CENTS_IN_RUB).toFixed();
};

export const convertNumberRublesToCents = (value: number): number => {
    if (!isFinite(value)) {
        return value;
    }
    return value * CENTS_IN_RUB;
};

type HaveClickmeConfig = (clickme: ClickmeConfig) => boolean;

export const haveClickmeConfig: HaveClickmeConfig = (clickme) => !!clickme?.email;

export const replaceDirectOnYandexDirect = (sources: CampaignSubSource[]): CampaignSubSource[] => {
    // если в РК есть Direct, то нужно заменить Direct на Yandex Direct
    // для корректного отображения в архивных РК
    const isOldDirectSaved = sources.includes(CampaignSubSource.Direct);

    if (isOldDirectSaved) {
        const newSources = sources.filter((source) => source !== CampaignSubSource.Direct);
        newSources.push(CampaignSubSource.YandexSearch);
        return newSources;
    }
    return sources;
};

export const setCurrentAccountIdToStorages = (value: string): void => {
    localStorageWrapper.setItem(CURRENT_ACCOUNT_ID_STORAGE, value);
    sessionStorageWrapper.setItem(CURRENT_ACCOUNT_ID_STORAGE, value);
};

type CheckStorageAccountId = (accounts: Account[], currentAccountId?: string) => void;

export const checkStorageAccountId: CheckStorageAccountId = (accounts, currentAccountId) => {
    if (accounts.length > 0) {
        const itemAccountIdFromLocal = localStorageWrapper.getItem(CURRENT_ACCOUNT_ID_STORAGE);
        const itemAccountIdFromSession = sessionStorageWrapper.getItem(CURRENT_ACCOUNT_ID_STORAGE);
        const availableAccountIds = accounts.map(({ id }) => `${id}`);

        const setFirstToAllStorage = () => {
            if (currentAccountId) {
                setCurrentAccountIdToStorages(currentAccountId);
                setAccountToHeader(currentAccountId);
            }
        };

        const checkAvailable = (accountId: string, isLocalStorage = false) => {
            if (availableAccountIds.includes(accountId)) {
                if (isLocalStorage) {
                    sessionStorageWrapper.setItem(CURRENT_ACCOUNT_ID_STORAGE, accountId);
                }
                setAccountToHeader(accountId);
            } else {
                setFirstToAllStorage();
            }
        };

        if (itemAccountIdFromSession) {
            checkAvailable(itemAccountIdFromSession);
        } else if (itemAccountIdFromLocal) {
            checkAvailable(itemAccountIdFromLocal, true);
        } else {
            setFirstToAllStorage();
        }
    } else {
        setCurrentAccountIdToStorages('');
    }
};

type ChangeAccount = (accountId: string, dashboardRoute: string) => void;

export const changeAccount: ChangeAccount = (accountId, dashboardRoute) => {
    setCurrentAccountIdToStorages(accountId);
    document.location.replace(dashboardRoute);
};

type SortByName = (users: UserFromAccounts[]) => UserFromAccounts[];

type SortEmployerUsers = (users: UserFromAccounts[], selectedUserIds?: string[]) => UserFromAccounts[];

const sortByName: SortByName = (users) => [...users].sort((a, b) => (a.name > b.name ? 1 : -1));

export const sortEmployerUsers: SortEmployerUsers = (users, selectedUserIds = []) => {
    if (selectedUserIds.length === 0) {
        return sortByName(users);
    }

    const selectedUsers: UserFromAccounts[] = [];
    const notSelectedUsers: UserFromAccounts[] = [];

    users.forEach((user) => {
        if (selectedUserIds.includes(user.id.toString())) {
            selectedUsers.push(user);
        } else {
            notSelectedUsers.push(user);
        }
    });

    return [...sortByName(selectedUsers), ...sortByName(notSelectedUsers)];
};

type GetImageFormat = (name: string) => string;

export const getImageFormat: GetImageFormat = (name) => name.split('.').slice(-1)[0];

type GetActiveCropTypes = (sources: Array<CampaignSource | CampaignSubSource>) => BannerImageCropType[];

// Возвращает список кропов, которые нужны для выбранных площадок
export const getActiveCropTypes: GetActiveCropTypes = (sources) => {
    if (sources.length === 0) {
        return BANNER_IMAGE_CROP_TYPES;
    }

    const newSources = [...sources];
    const currentCropTypes: BannerImageCropType[] = [BANNER_IMAGE_CROP_DEFAULT];

    BANNER_IMAGE_CROP_TYPES.forEach((cropType) => {
        BANNER_IMAGE_CROP_TYPES_CONFIG[cropType].sources.forEach((source) => {
            if (newSources.includes(source) && !currentCropTypes.includes(cropType)) {
                currentCropTypes.push(cropType);
            }
        });
    });
    return currentCropTypes;
};

export const checkAdditionalCropsNeeded = (
    sources: Array<CampaignSource | CampaignSubSource>,
    images: Images
): boolean => {
    const currentlyAvailableCrops: BannerImageCropType[] = [];

    for (const image in images) {
        if (images[image as BannerImageCropType].url) {
            currentlyAvailableCrops.push(image as BannerImageCropType);
        }
    }

    const hasHHCrop = currentlyAvailableCrops.includes(BannerImageCropType.HH);
    const hasVKCrop =
        currentlyAvailableCrops.includes(BannerImageCropType.VK) ||
        currentlyAvailableCrops.includes(BannerImageCropType.YandexAndVK);
    const hasYandexCrop = currentlyAvailableCrops.includes(BannerImageCropType.YandexAndVK);

    for (const source of sources) {
        // exp_PORTFOLIO-53635_next_line
        if ((source === CampaignSource.HH || HH_SUB_SOURCES.includes(source as CampaignSubSource)) && !hasHHCrop) {
            return true;
        }
        if (
            (source === CampaignSource.VKAds || VK_ADS_SUB_SOURCES.includes(source as CampaignSubSource)) &&
            !hasVKCrop
        ) {
            return true;
        }
        if (
            (source === CampaignSource.Yandex || YANDEX_SUB_SOURCES.includes(source as CampaignSubSource)) &&
            !hasYandexCrop
        ) {
            return true;
        }
    }

    return false;
};

export interface BannerImagesData {
    originalImageId: number;
    originalImageUrl: string;
    imageCropType: BannerImageCropType;
    width: number;
    height: number;
    cropStartX: number;
    cropStartY: number;
    cropHeight: number;
    cropWidth: number;
}

type GetAllCountCampaigns = (counts: Record<string, number>) => number;

export const getAllCountCampaigns: GetAllCountCampaigns = (counts) =>
    Object.keys(counts)
        .map((key) => counts[key])
        .reduce((sum, value) => sum + value);

type GetImageSize = (cropType: BannerImageCropType) => string[];

export const getImageSize: GetImageSize = (cropType) => [
    getFormattedIntNumber(BANNER_IMAGE_CROP_TYPES_CONFIG[cropType].minWidth),
    getFormattedIntNumber(BANNER_IMAGE_CROP_TYPES_CONFIG[cropType].minHeight),
];

type GetFileName = (name: string | number, url: string) => string;

export const getFileName: GetFileName = (name, url) => `${name}.${getImageFormat(url)}`;

type CheckImagesInCropLoaded = (images: Images, currentCropTypes: BannerImageCropType[]) => boolean;

export const checkImagesInCropLoaded: CheckImagesInCropLoaded = (images, currentCropTypes) =>
    currentCropTypes.every((cropType) => {
        const { id: isImageExist, isLoadedInCrop } = images[cropType];
        return !(isImageExist && !isLoadedInCrop);
    });

export const checkRequiredImagesUploaded: CheckImagesInCropLoaded = (images, currentCropTypes) =>
    currentCropTypes.every((cropType) => {
        const { id: isImageExist } = images[cropType];
        return isImageExist;
    });

export const getLoadingCropType = (images: Images): BannerImageCropType | null =>
    (Object.keys(images).find(
        (cropType) => images[cropType as BannerImageCropType].component === ImageComponent.Loading
    ) as BannerImageCropType) || null;

export const getInputCropTypes = (images: Images): BannerImageCropType[] =>
    Object.keys(images).filter(
        (cropType) => images[cropType as BannerImageCropType].component === ImageComponent.Input
    ) as BannerImageCropType[];

export const resetImageComponentByUpscaleStatus = (upscaleStatus: UpscaleStatus): ImageComponent =>
    upscaleStatus === UpscaleStatus.None ? ImageComponent.Input : ImageComponent.Crop;

export const resetUpscaleStatus = (upscaleStatus: UpscaleStatus): UpscaleStatus | undefined =>
    upscaleStatus === UpscaleStatus.None ? undefined : upscaleStatus;

type AddSearchParamsToUrl = (url: string) => string;

export const addSearchParamsToUrl: AddSearchParamsToUrl = (url) => `${url}${window.location.search}`;

type GetHHLoginLinkWithBackUrl = () => UrlObject;

export const getHHLoginLinkWithBackUrl: GetHHLoginLinkWithBackUrl = () => {
    const url = new URL(HH_LOGIN_LINK);
    url.searchParams.set(
        'backUrl',
        `https://clickme.hh.ru/new/login?from=${FROM_HH}&${window.location.search.slice(1)}`
    );
    return url;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const noop = (..._: unknown[]): void => void 0;

export const getArrayIntersection = <T, K>(a: T[], b: (T | K)[]): (T & K)[] =>
    a.filter((item) => b.includes(item)) as (T & K)[];

export { resolvePromisesMap } from 'utils/clickme/promiseUtils';
export { isObject } from 'utils/clickme/typeGuards';

export const getNullableNumberFromString = (value: string | null): number | null =>
    value?.trim() ? parseInt(value.trim(), 10) : null;
export const getStringFromNullableNumber = (value: number | null): string => String(value ?? '');

export const EMPTY_ARRAY = [];
export const EMPTY_OBJECT = {};

export const isAccountStatusReadOnly = (currentAccountStatus?: AccountStatus): boolean =>
    currentAccountStatus === AccountStatus.ReadOnly;

export const redirectIfReadOnly =
    (routePath: string, currentAccountStatus?: AccountStatus): AppThunkAction<void> =>
    (dispatch, getState) => {
        if (READ_ONLY_ROUTES.find((el) => el === routePath) && isAccountStatusReadOnly(currentAccountStatus)) {
            const { dynamicSettings } = getState();
            const dashboardRoute = getDashboardRoute(
                getSettingsValue(Settings.CampaignsDashboardRedesignAllowed, dynamicSettings) as boolean
            );
            dispatch(replace(dashboardRoute));
        }
    };

export const sleep = async (ms: number): Promise<unknown> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

export enum AvailableCurrencies {
    RUR = 'RUR',
    BYR = 'BYR',
}

export const CURRENCY_SYMBOLS = {
    [AvailableCurrencies.RUR]: '₽',
    [AvailableCurrencies.BYR]: 'Br',
};
